import React, {
  useRef,
  useContext,
  useCallback,
  MouseEvent as ReactMouseEvent,
  useMemo,
} from 'react';
import { ThemeProvider } from 'styled-components';
import { ButtonProps } from './types';
import { ButtonContainer, ButtonText, Button as StyledButton, Spinner } from './styles';
import BasicIcon from '../BasicIcon/BasicIcon';
import { ThemeContext } from '../ThemeProvider/ThemeProvider';

/**
 * @param label
 * @param onClick
 * @param type
 * @param isSubmitting
 * @param submitSuccess
 * @param submitFailure
 * @param disabled
 * @param icon
 */
const Button: React.FC<ButtonProps> = (props) => {
  const {
    label,
    onClick,
    type = 'button',
    status = 'default',
    hierarchy = 'primary',
    size = 'medium',
    isSubmitting,
    disabled,
    iconName,
    testId,
    width = '144',
    inverted = false,
  } = props;
  const { actualTheme } = useContext(ThemeContext);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const isIconButton = useMemo(() => iconName && !label, [iconName, label]);

  const handleClick = useCallback(
    (event: ReactMouseEvent<HTMLButtonElement, MouseEvent>) => {
      if (!isSubmitting && onClick) {
        event.stopPropagation();
        onClick(event);
      }
    },
    [isSubmitting, onClick],
  );

  const colorMap = {
    primary: actualTheme.status[status].background,
    secondary: actualTheme.status[status].default,
    minimal: 'inherit',
  };

  const color = colorMap[hierarchy] ?? actualTheme.button.defaultLinkColor;
  return (
    <ThemeProvider theme={actualTheme}>
      <StyledButton
        ref={buttonRef}
        id="button-id"
        type={type}
        onClick={handleClick}
        status={status}
        hierarchy={hierarchy}
        size={size}
        isSubmitting={isSubmitting}
        hasIcon={!!iconName}
        disabled={disabled}
        data-testid={testId}
        data-loader={iconName && isSubmitting}
        circular={iconName && !label}
        width={width}
        inverted={inverted}
        isIconButton={isIconButton}
      >
        {isSubmitting ? (
          <Spinner status={status} size={size} hierarchy={hierarchy} />
        ) : (
          <ButtonContainer>
            {iconName && (
              <BasicIcon color={color} name={iconName} size={size} data-testid={`${testId}-icon`} />
            )}
            {label && <ButtonText data-testid={`${testId}-label`}>{label}</ButtonText>}
          </ButtonContainer>
        )}
      </StyledButton>
    </ThemeProvider>
  );
};

export default Button;
